import React from "react";
import { Link } from "react-router-dom";
import "./styles/CloudInfrastructure.css";

const cloudModels = [
  {
    title: "Private Cloud",
    text: "Dedicated environments designed for workloads that require more control over data, infrastructure, and governance.",
  },
  {
    title: "Public Cloud",
    text: "Flexible infrastructure that scales on demand across leading cloud platforms, supporting fast growth and experimentation.",
  },
  {
    title: "Hybrid Cloud",
    text: "A coordinated mix of on premise, private, and public cloud resources to balance performance, cost, and control.",
  },
  {
    title: "Colocation",
    text: "Reliable, shared facilities that host your infrastructure close to cloud providers and critical network services.",
  },
];

const aiPoints = [
  {
    title: "Data ready foundation",
    text: "Cloud architectures that help prepare data for analytics and generative AI workloads across environments.",
  },
  {
    title: "Scalable compute",
    text: "Access to cloud resources that support demanding AI workloads without being limited by on premise capacity.",
  },
  {
    title: "Hybrid AI options",
    text: "Support for AI approaches that mix public cloud, private infrastructure, and colocation based deployments.",
  },
];

const connectivityItems = [
  {
    label: "Secure connectivity",
    text: "Network paths and controls designed to keep connectivity to AWS, Azure, and Google Cloud aligned with security and access needs.",
  },
  {
    label: "Multi cloud options",
    text: "Architectures that can connect to more than one cloud provider where that supports application or data strategy.",
  },
  {
    label: "Cloud adjacent design",
    text: "Colocation and network planning that bring infrastructure closer to cloud providers for lower latency and better performance.",
  },
];

const outcomeCards = [
  {
    title: "More flexible infrastructure choices",
    text: "Use the mix of private, public, hybrid, and colocation options that best match workload, governance, and growth needs.",
  },
  {
    title: "Stronger footing for AI initiatives",
    text: "Create an environment where generative AI solutions can run on data that is more accessible, organized, and secure.",
  },
  {
    title: "Better alignment with cloud platforms",
    text: "Connect more confidently to AWS, Azure, and Google Cloud with architectures built around secure access and performance.",
  },
];

const fitCards = [
  {
    title: "Multi environment IT teams",
    text: "For organizations operating across on-premise, private, and public cloud environments.",
  },
  {
    title: "Data and AI focused projects",
    text: "For teams planning or running analytics and generative AI workloads that depend on flexible infrastructure.",
  },
  {
    title: "Cloud modernization initiatives",
    text: "For businesses moving from legacy setups toward a more modern cloud and connectivity model.",
  },
];

export default function CloudInfrastructure() {
  return (
    <div className="cloudInfraPage">
      <section className="cloudInfraHero">
        <div className="cloudInfraContainer cloudInfraHero__container">
          <div className="cloudInfraHero__content">
            <p className="cloudInfraEyebrow">Cloud Infrastructure</p>

            <h1 className="cloudInfraHero__title">
              Design an infrastructure mix that is ready for cloud and AI.
            </h1>

            <p className="cloudInfraHero__text">
              We provide cloud infrastructure options across private, public,
              and hybrid models, along with reliable colocation services. Our
              generative AI–driven solutions help turn data into insight, and
              secure connectivity to AWS, Azure, and Google Cloud gives you
              seamless access to leading platforms.
            </p>

            <div className="cloudInfraHero__actions">
              <Link
                to="/contact"
                className="cloudInfraBtn cloudInfraBtn--primary"
              >
                Talk to Our Team
              </Link>
              <Link
                to="/services"
                className="cloudInfraBtn cloudInfraBtn--secondary"
              >
                Explore Services
              </Link>
            </div>
          </div>

          <div className="cloudInfraHero__visual">
            <div className="cloudDiagram">
              <div className="cloudDiagram__header">
                <span className="cloudTag">Cloud models</span>
                <span className="cloudTag cloudTag--soft">AI‑ready design</span>
              </div>

              <div className="cloudDiagram__grid">
                <div className="cloudCore">Your Environment</div>
                <div className="cloudProvider cloudProvider--aws">AWS</div>
                <div className="cloudProvider cloudProvider--azure">Azure</div>
                <div className="cloudProvider cloudProvider--gcp">Google Cloud</div>

                <span className="cloudLink cloudLink--aws" />
                <span className="cloudLink cloudLink--azure" />
                <span className="cloudLink cloudLink--gcp" />
              </div>

              <div className="cloudDiagram__footer">
                <div className="cloudMetric">
                  <strong>Private / public / hybrid</strong>
                  <span>Architectures designed around workload and governance needs</span>
                </div>
                <div className="cloudMetric">
                  <strong>Secure cloud connectivity</strong>
                  <span>Planned paths into AWS, Azure, and Google Cloud platforms</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="cloudModelsSection">
        <div className="cloudInfraContainer">
          <div className="cloudInfraSectionHead cloudInfraSectionHead--center">
            <p className="cloudInfraEyebrow">Cloud options</p>
            <h2 className="cloudInfraTitle">
              Choose the cloud mix that matches how your workloads operate
            </h2>
            <p className="cloudInfraText">
              Different applications, data sets, and teams benefit from
              different infrastructure models. A flexible cloud approach lets
              you align each with the right environment.
            </p>
          </div>

          <div className="cloudModelsGrid">
            {cloudModels.map((item) => (
              <article key={item.title} className="cloudModelCard">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="cloudAiSection">
        <div className="cloudInfraContainer cloudAiWrap">
          <div className="cloudAiIntro">
            <p className="cloudInfraEyebrow">Generative AI–driven solutions</p>
            <h2 className="cloudInfraTitle">
              An infrastructure foundation that supports AI and data initiatives
            </h2>
            <p className="cloudInfraText">
              Generative AI workloads depend on access to the right data,
              scalable compute, and a secure, well structured cloud
              environment. A thoughtful infrastructure design helps bring those
              elements together.
            </p>
          </div>

          <div className="cloudAiGrid">
            {aiPoints.map((item) => (
              <article key={item.title} className="cloudAiCard">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="cloudConnectivitySection">
        <div className="cloudInfraContainer">
          <div className="cloudInfraSectionHead cloudInfraSectionHead--center">
            <p className="cloudInfraEyebrow">Platform connectivity</p>
            <h2 className="cloudInfraTitle">
              Connectivity designed around AWS, Azure, and Google Cloud access
            </h2>
            <p className="cloudInfraText">
              Connecting to major cloud platforms is more effective when network
              paths, security, and architecture are planned together instead of
              as separate steps.
            </p>
          </div>

          <div className="cloudConnectivityGrid">
            {connectivityItems.map((item) => (
              <article key={item.label} className="cloudConnectivityCard">
                <strong>{item.label}</strong>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="cloudOutcomesSection">
        <div className="cloudInfraContainer">
          <div className="cloudInfraSectionHead">
            <p className="cloudInfraEyebrow">Business outcomes</p>
            <h2 className="cloudInfraTitle">
              What this cloud infrastructure approach helps improve
            </h2>
            <p className="cloudInfraText">
              The goal is to support day to day operations while also creating
              a platform that can adapt to new workloads and new ways of using
              data over time.
            </p>
          </div>

          <div className="cloudOutcomeGrid">
            {outcomeCards.map((item) => (
              <article key={item.title} className="cloudOutcomeCard">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="cloudFitSection">
        <div className="cloudInfraContainer">
          <div className="cloudInfraSectionHead cloudInfraSectionHead--center">
            <p className="cloudInfraEyebrow">Best fit</p>
            <h2 className="cloudInfraTitle">
              Where Cloud Infrastructure services are most useful
            </h2>
            <p className="cloudInfraText">
              These services are designed for teams that want to move beyond
              single environment setups toward more flexible, cloud aligned
              infrastructure.
            </p>
          </div>

          <div className="cloudFitGrid">
            {fitCards.map((item) => (
              <article key={item.title} className="cloudFitCard">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="cloudInfraCta">
        <div className="cloudInfraContainer cloudInfraCta__container">
          <div>
            <p className="cloudInfraEyebrow cloudInfraEyebrow--light">
              Next step
            </p>
            <h2 className="cloudInfraCta__title">
              Plan a cloud and connectivity strategy that fits your workloads
            </h2>
            <p className="cloudInfraCta__text">
              If you are evaluating private, public, and hybrid cloud options,
              colocation, or generative AI ready infrastructure, ComNetworks.net
              can help design a path that connects your environments with AWS,
              Azure, and Google Cloud.
            </p>
          </div>

          <div className="cloudInfraCta__actions">
            <Link
              to="/contact"
              className="cloudInfraBtn cloudInfraBtn--primary"
            >
              Contact Us
            </Link>
            <Link
              to="/services"
              className="cloudInfraBtn cloudInfraBtn--ghost"
            >
              View More Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
